/**
 * The application header. One row, dense, and the same on every screen.
 *
 * Theme and language are three-state and two-state respectively, both
 * persisted, both applied to <html> so a reload keeps them. The refresh
 * button is here because nothing refetches on focus (§13) -- staleness is a
 * deliberate default, so the escape from it has to be visible.
 *
 * Below `md` the inline theme and language controls collapse into the
 * Display sheet. Nothing is lost: the sheet carries the full set at every
 * width, and is the only place density lives. Refresh stays visible at
 * every width for the reason above -- it is the escape hatch, not a
 * setting.
 */
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { Languages, Monitor, Moon, RefreshCw, SlidersHorizontal, Sun } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'wouter'
import { IconButton, Label } from './ui/primitives.tsx'
import { Segmented } from './ui/tabs.tsx'
import { Dialog } from './ui/overlay.tsx'
import { useT } from '~/i18n/index.tsx'
import { useTheme, useDensity, type Theme, type Density } from '~/theme/provider.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { useAuth } from '~/lib/auth.tsx'
import { JobStrip } from '~/features/jobs/job-strip.tsx'

export function Header() {
  const { t, locale, setLocale, toggleLocale } = useT()
  const { theme, setTheme } = useTheme()
  const { density, setDensity } = useDensity()
  const qc = useQueryClient()
  const fetching = useIsFetching()
  const [path] = useLocation()
  const [showDisplay, setShowDisplay] = useState(false)

  const superadmin = useSuperadmin()
  const { user, logout } = useAuth()
  const nav: { href: string; label: string; match: (p: string) => boolean }[] = [
    { href: '/filter', label: t('nav.explore'), match: (p) => p.startsWith('/filter') },
    { href: '/results', label: t('nav.results'), match: (p) => p === '/' || p.startsWith('/results') || p.startsWith('/c/') },
    { href: '/about', label: t('nav.about'), match: (p) => p.startsWith('/about') },
    // Superadmin-only, like everything else it leads to.
    ...(superadmin
      ? [
          { href: '/gaps', label: t('corpus.gaps.title'), match: (p: string) => p === '/gaps' },
          { href: '/admin', label: t('admin.title'), match: (p: string) => p.startsWith('/admin') },
        ]
      : []),
  ]

  const themes: { id: Theme; label: React.ReactNode; title: string }[] = [
    { id: 'light', label: <Sun size={15} />, title: t('theme.light') },
    { id: 'dark', label: <Moon size={15} />, title: t('theme.dark') },
    { id: 'system', label: <Monitor size={15} />, title: t('theme.system') },
  ]

  const densities: { id: Density; label: string }[] = [
    { id: 'comfortable', label: t('density.comfortable') },
    { id: 'auto', label: t('density.auto') },
    { id: 'compact', label: t('density.compact') },
  ]

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-30 border-b border-[var(--border)]',
          'bg-[var(--bg-raised)]/95 backdrop-blur-[2px]',
          // The viewport meta opts into the notch region, so the bar has to
          // pad back out of it or the nav sits under the status bar.
          'pt-safe px-safe',
        )}
      >
        <div className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1 sm:gap-x-4 sm:px-3">
          <Link
            href="/"
            className={cn(
              'relative tap flex min-h-[var(--control-h-md)] shrink-0 items-center',
              'rounded-[var(--radius-control)] px-1',
              'text-[length:var(--text-data)] font-semibold tracking-tight text-[var(--text)]',
            )}
          >
            {t('app.name')}
          </Link>

          <nav className="flex min-w-0 items-center gap-0.5">
            {nav.map((n) => (
              <Link
                key={n.href}
                href={n.href}
                aria-current={n.match(path) ? 'page' : undefined}
                className={cn(
                  'relative tap inline-flex min-h-[var(--control-h-md)] items-center rounded-[var(--radius-control)]',
                  'px-2 text-[length:var(--text-data)] transition-colors duration-75',
                  'active:bg-[var(--bg-sunken)]',
                  n.match(path)
                    ? 'bg-[var(--bg-sunken)] font-medium text-[var(--text)]'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-sunken)] hover:text-[var(--text)]',
                )}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          {/*
            In the header, not on the gaps screen, and that is the point: the
            requirement is that a person can leave the page. A progress bar
            that vanishes on navigation reads as "the work stopped", and no
            reassurance on the screen you just left can undo that.
          */}
          <JobStrip />

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Who you are, and the way out. Kept beside the other chrome
                rather than in a menu: on an app with two roles, the role is
                worth seeing without a click. */}
            {user && (
              <span className="hidden items-baseline gap-1.5 sm:flex">
                <span className="text-[length:var(--text-micro)] text-[var(--text-muted)]">
                  {user.displayName || user.username}
                </span>
                {user.role === 'admin' && (
                  <span className="text-[length:var(--text-micro)] text-[var(--accent)]">
                    {t('admin.role.admin')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void logout()}
                  className="link-inline text-[length:var(--text-micro)] text-[var(--text-faint)]"
                >
                  {t('auth.signOut')}
                </button>
              </span>
            )}

            <IconButton
              label={t('action.refresh')}
              onClick={() => void qc.refetchQueries()}
            >
              <RefreshCw size={16} className={cn(fetching > 0 && 'animate-spin')} />
            </IconButton>

            {/*
              Inline on a wide screen; inside the sheet on a narrow one.

              Hidden by a *wrapper*, not by a `hidden` class on the control:
              `cn` is deliberately not tailwind-merge (see lib/cn.ts), and
              both Button and Segmented already carry `inline-flex`, which
              Tailwind emits after `.hidden` and therefore wins. Conflicting
              utilities are a mistake to fix, not a runtime problem to paper
              over -- so the wrapper does the hiding.
            */}
            <div className="hidden items-center gap-1 sm:gap-2 md:flex">
              <IconButton
                label={`${t('lang.label')}: ${locale === 'en' ? t('lang.ar') : t('lang.en')}`}
                onClick={toggleLocale}
              >
                <Languages size={16} />
              </IconButton>

              <Segmented
                ariaLabel={t('theme.label')}
                options={themes}
                value={theme}
                onChange={setTheme}
              />
            </div>

            <IconButton
              label={t('display.label')}
              active={showDisplay}
              onClick={() => setShowDisplay(true)}
            >
              <SlidersHorizontal size={16} />
            </IconButton>
          </div>
        </div>
      </header>

      {/*
        One sheet, every width. On a phone it is the only way to reach theme
        and language; on a desktop it is the only way to reach density. Both
        audiences find the same control in the same place.
      */}
      <Dialog
        open={showDisplay}
        onClose={() => setShowDisplay(false)}
        title={t('display.title')}
        closeLabel={t('action.close')}
        width={420}
      >
        <div className="flex flex-col gap-5">
          <section className="flex flex-col gap-2">
            <Label>{t('theme.label')}</Label>
            <Segmented
              fill
              ariaLabel={t('theme.label')}
              options={themes.map((x) => ({ ...x, label: <SheetOption icon={x.label} text={x.title} /> }))}
              value={theme}
              onChange={setTheme}
            />
          </section>

          <section className="flex flex-col gap-2">
            <Label>{t('lang.label')}</Label>
            <Segmented
              fill
              ariaLabel={t('lang.label')}
              options={[
                { id: 'en' as const, label: t('lang.en') },
                { id: 'ar' as const, label: t('lang.ar') },
              ]}
              value={locale}
              onChange={setLocale}
            />
          </section>

          <section className="flex flex-col gap-2">
            <Label>{t('density.label')}</Label>
            <Segmented
              fill
              ariaLabel={t('density.label')}
              options={densities}
              value={density}
              onChange={setDensity}
            />
            <p className="text-[length:var(--text-tiny)] leading-[var(--text-tiny--line-height)] text-[var(--text-faint)]">
              {t('density.autoHint')}
            </p>
          </section>
        </div>
      </Dialog>
    </>
  )
}

/** An icon and its name, so the sheet does not present three bare glyphs. */
function SheetOption({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {icon}
      {text}
    </span>
  )
}
